import { FOOTER_PREMIUM } from "../common/footer";
import { PULSE_HEADER } from "../common/header";

/**
 * World-Class Performance Report Template
 * Features: Overall score ring, KPI cards with status badges, bot comparison, improvement priorities
 */
export const performanceReport = `
<mjml>
  <mj-head>
    <mj-title>Performance Report - {{report_period}}</mj-title>
    <mj-preview>Track your chatbot's performance metrics and KPIs</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .status-excellent { background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700; }
      .status-good { background: linear-gradient(135deg, #3B82F6, #2563EB); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700; }
      .status-needs-attention { background: linear-gradient(135deg, #EF4444, #DC2626); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f1f5f9">
    \${PULSE_HEADER("Performance Report", "{{report_period}}")}

    <!-- Introduction -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="24px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Hello {{first_name}},
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          Here's your detailed performance report for {{product_name}}. This report analyzes key performance indicators to help you optimize your chatbot experience.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Overall Performance Score -->
    <mj-section background-color="#1939B7" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="rgba(255,255,255,0.8)" text-transform="uppercase" letter-spacing="2px" align="center" padding-bottom="15px">
          Overall Performance Score
        </mj-text>
        <mj-text font-size="72px" color="#ffffff" font-weight="800" align="center" padding="0" line-height="1">
          {{overall_score}}
        </mj-text>
        <mj-text font-size="18px" color="rgba(255,255,255,0.7)" align="center" padding-top="5px" padding-bottom="15px">
          out of 100
        </mj-text>
        <mj-text align="center">
          <span class="{{performance_status_class}}">{{performance_status}}</span>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- KPI Header -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Key Indicators
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700">
          KPI Breakdown
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- KPI 1: Response Time -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 50px; vertical-align: middle;">
                <span style="background: linear-gradient(135deg, #f0f9ff, #e0f2fe); width: 44px; height: 44px; border-radius: 10px; display: inline-block; line-height: 44px; text-align: center; font-size: 20px;">&#9889;</span>
              </td>
              <td style="vertical-align: middle; padding-left: 15px;">
                <strong style="font-size: 16px; color: #0f172a;">Response Time</strong><br>
                <span style="color: #64748b; font-size: 13px;">Average time to first response</span>
              </td>
              <td style="text-align: center; vertical-align: middle; width: 80px;">
                <span style="font-size: 24px; font-weight: 700; color: #0EA5E9;">{{response_time_value}}</span>
              </td>
              <td style="text-align: right; vertical-align: middle; width: 90px;">
                <span class="{{response_time_status_class}}">{{response_time_status}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- KPI 2: Resolution Rate -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 50px; vertical-align: middle;">
                <span style="background: linear-gradient(135deg, #ecfdf5, #d1fae5); width: 44px; height: 44px; border-radius: 10px; display: inline-block; line-height: 44px; text-align: center; font-size: 20px;">&#10003;</span>
              </td>
              <td style="vertical-align: middle; padding-left: 15px;">
                <strong style="font-size: 16px; color: #0f172a;">Resolution Rate</strong><br>
                <span style="color: #64748b; font-size: 13px;">Queries resolved without escalation</span>
              </td>
              <td style="text-align: center; vertical-align: middle; width: 80px;">
                <span style="font-size: 24px; font-weight: 700; color: #10B981;">{{resolution_rate}}%</span>
              </td>
              <td style="text-align: right; vertical-align: middle; width: 90px;">
                <span class="{{resolution_status_class}}">{{resolution_status}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- KPI 3: User Satisfaction -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 50px; vertical-align: middle;">
                <span style="background: linear-gradient(135deg, #fef3c7, #fde68a); width: 44px; height: 44px; border-radius: 10px; display: inline-block; line-height: 44px; text-align: center; font-size: 20px;">&#11088;</span>
              </td>
              <td style="vertical-align: middle; padding-left: 15px;">
                <strong style="font-size: 16px; color: #0f172a;">User Satisfaction</strong><br>
                <span style="color: #64748b; font-size: 13px;">Based on feedback ratings</span>
              </td>
              <td style="text-align: center; vertical-align: middle; width: 80px;">
                <span style="font-size: 24px; font-weight: 700; color: #F59E0B;">{{satisfaction_score}}/5</span>
              </td>
              <td style="text-align: right; vertical-align: middle; width: 90px;">
                <span class="{{satisfaction_status_class}}">{{satisfaction_status}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- KPI 4: Engagement Rate -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 50px; vertical-align: middle;">
                <span style="background: linear-gradient(135deg, #faf5ff, #ede9fe); width: 44px; height: 44px; border-radius: 10px; display: inline-block; line-height: 44px; text-align: center; font-size: 20px;">&#128172;</span>
              </td>
              <td style="vertical-align: middle; padding-left: 15px;">
                <strong style="font-size: 16px; color: #0f172a;">Engagement Rate</strong><br>
                <span style="color: #64748b; font-size: 13px;">Users who interact beyond first message</span>
              </td>
              <td style="text-align: center; vertical-align: middle; width: 80px;">
                <span style="font-size: 24px; font-weight: 700; color: #8B5CF6;">{{engagement_rate}}%</span>
              </td>
              <td style="text-align: right; vertical-align: middle; width: 90px;">
                <span class="{{engagement_status_class}}">{{engagement_status}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- KPI 5: Conversion Rate -->
    <mj-section background-color="#ffffff" padding="10px 30px 30px" border-radius="0 0 16px 16px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 50px; vertical-align: middle;">
                <span style="background: linear-gradient(135deg, #fce7f3, #fbcfe8); width: 44px; height: 44px; border-radius: 10px; display: inline-block; line-height: 44px; text-align: center; font-size: 20px;">&#127919;</span>
              </td>
              <td style="vertical-align: middle; padding-left: 15px;">
                <strong style="font-size: 16px; color: #0f172a;">Conversion Rate</strong><br>
                <span style="color: #64748b; font-size: 13px;">Leads/goals achieved from conversations</span>
              </td>
              <td style="text-align: center; vertical-align: middle; width: 80px;">
                <span style="font-size: 24px; font-weight: 700; color: #EC4899;">{{conversion_rate}}%</span>
              </td>
              <td style="text-align: right; vertical-align: middle; width: 90px;">
                <span class="{{conversion_status_class}}">{{conversion_status}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Bot Performance Comparison -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Comparison
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Performance by Chatbot
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 2px solid #e2e8f0; background-color: #f8fafc;">
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b; text-transform: uppercase;">Chatbot</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Score</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Chats</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Rating</th>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #1939B7; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">1</span>
              {{bot_1_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 700; color: #1939B7;">{{bot_1_score}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_1_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #F59E0B;">{{bot_1_satisfaction}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #64748B; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">2</span>
              {{bot_2_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 700; color: #1939B7;">{{bot_2_score}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_2_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #F59E0B;">{{bot_2_satisfaction}}</td>
          </tr>
          <tr>
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #94A3B8; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">3</span>
              {{bot_3_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 700; color: #1939B7;">{{bot_3_score}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_3_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #F59E0B;">{{bot_3_satisfaction}}</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Areas for Improvement -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Action Items
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Areas for Improvement
        </mj-text>
        <mj-text font-size="14px" color="#334155" padding-bottom="15px">
          <table style="width: 100%;">
            <tr>
              <td style="width: 30px; vertical-align: top;">
                <span style="background: #EF4444; color: white; width: 22px; height: 22px; border-radius: 6px; display: inline-block; text-align: center; line-height: 22px; font-size: 11px; font-weight: 700;">P1</span>
              </td>
              <td style="padding-left: 10px; vertical-align: top;">{{improvement_1}}</td>
            </tr>
          </table>
        </mj-text>
        <mj-text font-size="14px" color="#334155" padding-bottom="15px">
          <table style="width: 100%;">
            <tr>
              <td style="width: 30px; vertical-align: top;">
                <span style="background: #F59E0B; color: white; width: 22px; height: 22px; border-radius: 6px; display: inline-block; text-align: center; line-height: 22px; font-size: 11px; font-weight: 700;">P2</span>
              </td>
              <td style="padding-left: 10px; vertical-align: top;">{{improvement_2}}</td>
            </tr>
          </table>
        </mj-text>
        <mj-text font-size="14px" color="#334155">
          <table style="width: 100%;">
            <tr>
              <td style="width: 30px; vertical-align: top;">
                <span style="background: #3B82F6; color: white; width: 22px; height: 22px; border-radius: 6px; display: inline-block; text-align: center; line-height: 22px; font-size: 11px; font-weight: 700;">P3</span>
              </td>
              <td style="padding-left: 10px; vertical-align: top;">{{improvement_3}}</td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#1939B7" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Optimize Your Performance
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          Access detailed analytics and implement improvements to boost your scores.
        </mj-text>
        <mj-button background-color="#2DD4BF" color="#0F172A" href="{{dashboard_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          View Performance Dashboard &#8594;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "performanceReport",
  name: "Performance Report - Premium",
  category: "pulse",
  purpose: "pulse" as const,
  description:
    "A world-class performance report with bold overall score display, KPI cards with gradient icons and status badges, bot comparison table, and prioritized improvement areas.",
  features: [
    "Bold overall score with status badge",
    "KPI cards with gradient icons",
    "Status badges (Excellent/Good/Needs Attention)",
    "Bot-wise performance comparison",
    "Priority-coded improvement areas",
    "Premium CTA styling",
  ],
  subject: "&#128200; Performance Report - {{report_period}}",
  subTitle: "Track your chatbot's performance metrics and KPIs",
  mjmlTemplate: performanceReport,
  tags: ["pulse", "performance", "kpi", "metrics", "report", "optimization"],
};
